import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import { AuthService } from 'app/service/auth-service';

export interface RestServiceConfig {
    abortController: AbortController;
}

class RestService {
    // TODO: change `api` to proper production path
    private static readonly BE_BASE_URL: string = '/api';

    // aliases, less used
    public post: typeof RestService.prototype.save = this.save;
    public put: typeof RestService.prototype.update = this.update;

    private readonly axiosInstance: AxiosInstance;

    private readonly abortController: AbortController;

    public constructor(config: RestServiceConfig | Omit<RestServiceConfig, 'abortController'>) {
        const updatedConfig = {
            abortController: new AbortController(),
            ...config,
        };

        this.abortController = updatedConfig.abortController;

        this.axiosInstance = axios.create({
            baseURL: RestService.BE_BASE_URL,
            signal: this.abortController.signal,
            // Uncomment adapter if you need to use mock data
            // adapter: _config => mockedDataAdapter(_config),
        });

        this.axiosInstance.interceptors.request.use((request) => {
            // config request here
            const token: string | undefined = AuthService.getAccessToken();

            if (request.headers && token) {
                request.headers.Authorization = `Bearer ${token}`;
            }
            return request;
        });

        this.axiosInstance.interceptors.response.use(
            (response) => response.data,
            (error) => {
                // handle exceptional BE errors here
                if (error.response.status === 401) {
                    AuthService.authorize();
                }
                return Promise.reject(error.response);
            },
        );
    }

    public get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return this.axiosInstance.get(url, config);
    }

    public save<T>(url: string, data?: object, config?: AxiosRequestConfig): Promise<T> {
        return this.axiosInstance.post(url, data, config);
    }

    public delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return this.axiosInstance.delete(url, config);
    }

    public update<T>(url: string, data?: object, config?: AxiosRequestConfig): Promise<T> {
        return this.axiosInstance.put(url, data, config);
    }

    public patch<T>(url: string, data?: object, config?: AxiosRequestConfig): Promise<T> {
        return this.axiosInstance.patch(url, data, config);
    }
}

const restService: RestService = new RestService({});

export { restService, RestService };
